import { allows, rightsOf, unitsOf } from '../../lib/permissions';
import { normalizeLinks } from '../../lib/links';
import { storedDocuments } from '../../lib/documents';

// Documents that cover a period (operative programme, duty rosters): startDate and endDate as
// "YYYY-MM-DD", a unit (service) and documents of the service's Dropbox. Former entries hold
// one Dropbox link (name, link), often without dates nor unit.

// Days are handled as "YYYY-MM-DD" in local time (no shift across time zones).
const pad = (value) => String(value).padStart(2, '0');
const keyOf = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
export const dayOf = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ''));
  if (match) return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? new Date(date.getFullYear(), date.getMonth(), date.getDate()) : null;
};
export const toKey = (value) => {
  const date = dayOf(value);
  return date ? keyOf(date) : '';
};
export const todayKey = () => keyOf(new Date());

export const periodOf = (item) => {
  const start = toKey(item.startDate);
  if (!start) return null;
  const end = toKey(item.endDate);
  return { start, end: end && end >= start ? end : start };
};

const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1);
// "Lundi 29 septembre 2026" or "Du lun. 29 sept. au ven. 3 oct. 2026"
export const periodLabel = (period) => {
  if (!period) return 'Date non précisée';
  const start = dayOf(period.start);
  const end = dayOf(period.end);
  if (period.start === period.end) return capitalize(start.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }));
  const short = { weekday: 'short', day: 'numeric', month: 'short' };
  const from = start.toLocaleDateString('fr-FR', start.getFullYear() === end.getFullYear() ? short : { ...short, year: 'numeric' });
  return `Du ${from} au ${end.toLocaleDateString('fr-FR', { ...short, year: 'numeric' })}`;
};

export const documentsOf = (item) => storedDocuments(item.documents);
// Former Dropbox links: the single link of the first pages, or those chosen before the
// service's Dropbox was linked.
export const legacyLinks = (item) => [
  ...(item.link ? normalizeLinks([{ link: item.link, name: item.name }]) : []),
  ...normalizeLinks(item.dropboxLinks),
];
export const titleOf = (item, fallback = 'Document') => String(item.title || '').trim() || item.name || (documentsOf(item)[0] || {}).name || fallback;

// Actions follow the level on the section (rights grid), each unit for its own entries; the
// former entries, shared by every unit, stay editable by any of them.
export const scheduleAccess = (user, section) => {
  const rights = rightsOf(user);
  const isAdmin = user.role === 'Admin';
  const myUnits = unitsOf(user.service);
  const owns = (item) => isAdmin || !item.service || unitsOf(item.service).some((unit) => myUnits.includes(unit));
  return {
    isAdmin,
    myUnits,
    canAdd: allows(rights, section, 'modifier'),
    canEdit: (item) => allows(rights, section, 'modifier') && owns(item),
    canDelete: (item) => allows(rights, section, 'supprimer') && owns(item),
    canDownload: allows(rights, section, 'tout'),
  };
};
